import { useEffect } from 'react';

import { useMergedRef } from '@/hook';

import { TRACK_TYPE, VBaseTrack, type VBaseTrackProps } from '../VBaseTrack';
import {
  SLIDER_VARIANT,
  VSliderContextProvider,
  VSliderFilledTrackList,
  VSliderMarkerContainer,
  VSliderThumb,
  VSliderTrack,
} from '../VSlider';
import { useVSliderContext } from '../VSlider/hook';
import { VStack } from '../VStack';
import { new__cn as cn } from '../util';
import { VFrameSliderContextProvider } from './VFrameSliderContextProvider';
import { VFrameSliderThumb } from './VFrameSliderThumb';
import { ARIA_LABEL, FRAME_SLIDER_SCALE } from './constant';
import { useVFrameSliderContext } from './hook';
import type { FrameSliderRange, VFrameSliderProps } from './type';
import { getStepConfig } from './util';

const VInnerFrameSlider = ({
  className,
  ref,
  ...rest
}: React.ComponentPropsWithRef<'div'>) => {
  const { normalizedValue, style, $container } =
    useVSliderContext<TRACK_TYPE.RANGE>();
  const range = normalizedValue[0] as Nullish<FrameSliderRange>;
  const { value, setRange, setValue } = useVFrameSliderContext();
  const mergedRef = useMergedRef($container, ref);
  const hasValidRange = range?.length === 2 && range[0] !== range[1];

  useEffect(() => {
    if (hasValidRange) setRange(range as FrameSliderRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidRange, range]);

  return (
    <VStack
      aria-label={ARIA_LABEL.FRAME_SLIDER}
      className={cn(style.container, className, 'w-full')}
      role="slider"
      gap={0.25}
      ref={mergedRef}
      {...rest}
    >
      <VSliderTrack onTrackClick={value => setValue(value, 'track')}>
        <VSliderFilledTrackList />
      </VSliderTrack>

      {hasValidRange && (
        <div>
          {range.map(value => (
            <VSliderThumb key={value} thumbValue={value} />
          ))}
        </div>
      )}

      <VFrameSliderThumb thumbValue={value} />
      <VSliderMarkerContainer
        markerClassName={(_, isInRange) =>
          cn({
            'text-gray-300': range?.length === 2 && !isInRange,
          })
        }
        onMarkerClick={value => setValue(value, 'marker')}
      />
    </VStack>
  );
};

export const VFrameSlider = ({
  autoTracking = true,
  disabled,
  marker = true,
  max,

  min,
  range,
  readOnly,
  scale = FRAME_SLIDER_SCALE.SCALE_1,
  value = 1,
  variant = SLIDER_VARIANT.SECONDARY,
  onChange,
  onRangeChange,
  onScaleChange,
  ...rest
}: VFrameSliderProps &
  Omit<React.ComponentPropsWithRef<'div'>, 'onChange'>) => {
  const baseTrackProps = {
    disabled,
    max,
    min,
    readOnly,
    step: getStepConfig(scale).step,
    type: TRACK_TYPE.RANGE,
    value: (range as VBaseTrackProps<TRACK_TYPE.RANGE>['value']) ?? [],
    onChange: onRangeChange as VBaseTrackProps<TRACK_TYPE.RANGE>['onChange'],
  } as VBaseTrackProps<TRACK_TYPE.RANGE>;

  return (
    <VBaseTrack {...baseTrackProps}>
      <VSliderContextProvider
        marker={marker}
        variant={SLIDER_VARIANT.FRAME_SLIDER}
      >
        <VFrameSliderContextProvider
          autoTracking={autoTracking}
          scale={scale}
          value={value}
          variant={variant}
          onChange={onChange}
          onScaleChange={onScaleChange}
        >
          <VInnerFrameSlider {...rest} />
        </VFrameSliderContextProvider>
      </VSliderContextProvider>
    </VBaseTrack>
  );
};

VFrameSlider.displayName = 'VFrameSlider';
